import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { emptyTableCellClassName } from '@/constants/ui'
import { listPdAccessLog } from '@/api/pd-log'
import { formatMoscowDateTime } from '@/utils/format'
import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import { SearchInput } from '@/components/ui/SearchInput'
import { PageHeader } from '@/components/layout/PageHeader'
import type { PdAccessLogRow } from '@/types/audit'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

function userName(r: PdAccessLogRow): string {
  if (r.user_name && r.user_name.length > 0) return r.user_name
  if (USE_MOCK) {
    const p = mockState.profiles.find((p) => p.id === r.user_id)
    if (p?.full_name) return p.full_name
  }
  return r.user_id || '—'
}

export function PdLogPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 200)

  const { data } = useQuery({
    queryKey: ['pd-log'],
    queryFn: () => listPdAccessLog({ page: 1, pageSize: 50 }),
  })

  const allRows = data?.items ?? []
  const q = debouncedSearch.toLowerCase()
  const rows = q
    ? allRows.filter(
        (r) =>
          userName(r).toLowerCase().includes(q) ||
          r.order_id.toLowerCase().includes(q),
      )
    : allRows

  return (
    <div>
      <PageHeader
        title={t('logs.pd.title')}
        filters={
          <SearchInput
            placeholder={t('logs.pd.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        }
      />
      <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2">{t('logs.pd.colDate')}</th>
              <th className="px-3 py-2">{t('logs.pd.colUser')}</th>
              <th className="px-3 py-2">{t('logs.pd.colOrder')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--stryzh-color-border-default)]">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatMoscowDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2">{userName(r)}</td>
                  <td className="px-3 py-2">
                    <Link
                      className="text-[var(--stryzh-color-accent-orange)] hover:underline"
                      to={`/orders/${r.order_id}`}
                    >
                      {t('logs.pd.open')}
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
